import { MutableRefObject, useLayoutEffect, useRef } from 'react';

enum MouseButton {
  LeftClick = 1,
  MouseWheel = 4,
}

const DRAG_BUTTONS = new Set([MouseButton.LeftClick, MouseButton.MouseWheel]);

export type AttachUndraggable<Element extends HTMLElement = HTMLDivElement> = (
  element: Element,
) => void;

/**
 * Make an element scrollable by dragging.
 */
export default function useDragScroll<Element extends HTMLElement = HTMLDivElement>(
  panScreenRef?: MutableRefObject<(transformX: number, transformY: number) => unknown>,
): [MutableRefObject<Element>, AttachUndraggable<Element>] {
  // Ref to be attached to the element we want to drag
  const scrollRef = useRef<Element>();
  const isScrollingRef = useRef<boolean>(false);

  // Undraggable objects
  const undraggables = useRef<Element[]>([]);
  const attachUndraggable: AttachUndraggable<Element> = (ref) => {
    // crazy for performance
    if (ref && !undraggables.current.some((undraggable) => undraggable === ref)) {
      undraggables.current.push(ref);
    }
  };

  // Position of the mouse on the page on mousedown
  const startXRef = useRef<number>(0);
  const startYRef = useRef<number>(0);
  // Amount the draggable element is already scrolled
  const startScrollLeftRef = useRef<number>(0);
  const startScrollTopRef = useRef<number>(0);

  const draggingElement = useRef<EventTarget>();

  // useLayoutEffect > useEffect because we measure the DOM
  useLayoutEffect(() => {
    // Note these are the native HTML event types, not the React versions.
    // This is because we're attaching them via addEventListener.
    const isUndraggable = (e: MouseEvent): boolean => {
      return undraggables.current.some((undraggable) => {
        return undraggable.contains(e.target as Node);
      });
    };

    function handleDown(e: MouseEvent) {
      // Only allow dragging inside of target element
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (!scrollRef.current.contains(e.target as Node) || isUndraggable(e) || e.shiftKey) {
        return;
      }

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      draggingElement.current = e.target;

      // Set initial positions of mouse element scroll
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      startXRef.current = e.pageX - scrollRef.current.offsetLeft;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      startYRef.current = e.pageY - scrollRef.current.offsetTop;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      startScrollLeftRef.current = scrollRef.current.scrollLeft;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      startScrollTopRef.current = scrollRef.current.scrollTop;

      if (panScreenRef?.current) {
        startXRef.current = e.pageX;
        startYRef.current = e.pageY;
        document.addEventListener('mouseup', handleUp);
        document.addEventListener('mousemove', handleMove);
      }
    }

    function handleUp(_e: MouseEvent) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      draggingElement.current = null;
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    }

    function handleMove(e: MouseEvent) {
      // Don't fire if other buttons are pressed
      if (
        !DRAG_BUTTONS.has(e.buttons) ||
        !scrollRef.current?.contains(e.target as Node) ||
        isUndraggable(e) ||
        draggingElement.current == null
      ) {
        return;
      }

      e.preventDefault();

      if (!isScrollingRef.current) {
        // Use RAF to avoid overloading the browser with drag event handling
        window.requestAnimationFrame(() => {
          if (panScreenRef?.current) {
            // Position of mouse on the page
            const mouseX = e.pageX;
            const mouseY = e.pageY;

            // Distance of the mouse from the origin of the last mousedown event
            const walkX = mouseX - startXRef.current;
            const walkY = mouseY - startYRef.current;

            // pan screen
            panScreenRef.current(walkX, walkY);

            // set current x y
            startXRef.current = mouseX;
            startYRef.current = mouseY;

            isScrollingRef.current = false;
          } else {
            // Position of mouse on the page
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            const mouseX = e.pageX - scrollRef.current.offsetLeft;
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            const mouseY = e.pageY - scrollRef.current.offsetTop;

            // Distance of the mouse from the origin of the last mousedown event
            const walkX = mouseX - startXRef.current;
            const walkY = mouseY - startYRef.current;

            // Set element scroll
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            scrollRef.current.scrollLeft = startScrollLeftRef.current - walkX;
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            scrollRef.current.scrollTop = startScrollTopRef.current - walkY;

            isScrollingRef.current = false;
          }
        });
      }

      isScrollingRef.current = true;
    }

    // Add and clean up listeners
    document.addEventListener('mousedown', handleDown);
    return () => {
      document.removeEventListener('mousedown', handleDown);
    };
  });

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return [scrollRef, attachUndraggable];
}
